/*
 * Copyright (c) 2026, Gather
 * https://gather.life
 */

import {
  type CatalogFilterState,
  createDefaultYearState,
} from '@/page/catalog-vehicles/store/catalog-filter.store';

import {
  concatReadonlyArrays,
  type EncoderFn,
  encodeRangeNumber,
  encodeMappedFilter,
  encodeArrayFilter,
  formatThousand,
  encodeColor,
} from '.';

type Filters = CatalogFilterState['filters'];

export function encodeYear(filter: Filters['year']): EncoderFn {
  return encodeRangeNumber(filter, undefined, createDefaultYearState());
}

export function encodeMakeModel(filter: Filters['makeModels'], query: string): EncoderFn {
  const normalizedQuery = query.toLowerCase();

  return (values) =>
    concatReadonlyArrays(
      values,
      filter.reduce<string[]>((parts, makeModel) => {
        const makeFound = normalizedQuery.includes(makeModel.make.toLowerCase());
        const unmatched = (makeModel.modelTrims ?? [])
          .map(({ model: modelName }) => modelName)
          .filter((modelName) => !normalizedQuery.includes(modelName.toLowerCase()));

        return makeFound ? [...unmatched, ...parts] : [...parts, makeModel.make, ...unmatched];
      }, [])
    );
}

export function encodeBodyType(filter: Filters['bodyType']): EncoderFn {
  return encodeMappedFilter<(typeof filter)[number]>(filter, (bodyType) => bodyType.name);
}

export const encodeListingType = encodeArrayFilter;

export function encodePrice(filter: Filters['price']): EncoderFn {
  return encodeRangeNumber(filter, (value) =>
    formatThousand(value, { style: 'currency', currency: 'USD' })
  );
}

export function encodeMileage(filter: Filters['mileage']): EncoderFn {
  return encodeRangeNumber(filter, (value) => formatThousand(value), undefined, 'miles');
}

// AKA: Vehicle Condition
export const encodeInventoryType = encodeArrayFilter;

export const encodeExteriorColor = encodeColor;
export const encodeInteriorColor = encodeColor;

export const encodeFuelType = encodeArrayFilter;
export const encodeTransmission = encodeArrayFilter;
export const encodeDrivetrain = encodeArrayFilter;

export function encodeSeatingCapacity(filters: Filters['seatingCapacity']): EncoderFn {
  return (values) =>
    filters.reduce(
      (parts, filter) =>
        encodeRangeNumber(
          filter,
          undefined,
          undefined,
          `seat${Math.min(filter.min ?? 0, filter.max ?? 0) > 1 ? 's' : ''}`
        )(parts),
      values
    );
}

export function encodeFeatures(filters: Filters['features']): EncoderFn {
  return (values) =>
    Object.values(filters)
      .filter((filter) => filter != null)
      .reduce((parts, filter) => encodeArrayFilter(filter)(parts), values);
}
